import { env } from "@/config/env";
import { CLIENT_SUFFIX_HEADER, getClientSuffix } from "@/config/tenant";
import { SESSION_COOKIE } from "@/config/cookies";
import { isNestEnvelope } from "@/shared/http/nest-envelope";
import { normalizeApiMessage } from "@/shared/helpers/errors";
import { ensureSessionId } from "@/shared/http/session-id";
import { getServiceSessionId, invalidateServiceSession } from "@/shared/http/service-session";

/**
 * Cliente de LEITURA PÚBLICA da nest — catálogo e configuração de loja.
 *
 * Existe separado do `http` (axios) por um motivo só, e ele é o que decide se a
 * resposta pode ser compartilhada entre visitantes:
 *
 *   o `http` anexa IDENTIDADE no SSR (Bearer do cookie httpOnly + um
 *   `x-session-id` por visitante). Com isso o backend devolve a variante
 *   PERSONALIZADA do payload — `isFavorite` por usuário — e o HTML renderizado
 *   passa a conter estado de um cliente específico. Isso (a) impede qualquer
 *   cache de HTML e (b) VIRA VAZAMENTO se alguém ligar cache antes de corrigir.
 *
 * Aqui a leitura é ANÔNIMA por construção: nenhum token, e um `x-session-id`
 * constante. O backend só computa favoritos quando há usuário autenticado
 * (`product.service.ts`), então pelo caminho anônimo `isFavorite` volta sempre
 * `false` e o HTML de um visitante logado é idêntico ao de um anônimo. O coração
 * preenchido vira ilha client — ver `@/modules/favorites`.
 *
 * Use este cliente para tudo que é público (widget, categoria, produto,
 * listagem, busca, settings, info-pages). O `http` continua sendo o caminho de
 * rotas PRIVADAS (perfil, pedidos, favoritos) e de MUTAÇÕES.
 *
 * ── Multi-tenant ──────────────────────────────────────────────────────────
 * O suffix é PARÂMETRO, nunca configuração: ele viaja no header e o Next inclui
 * os headers na chave do Data Cache, então cada tenant ganha sua própria
 * entrada. As `tags` recebem o prefixo do suffix AQUI DENTRO, para que nenhum
 * chamador possa esquecer e um `revalidateTag` de um cliente invalidar o cache
 * dos outros.
 */

/**
 * Janela máxima (segundos) para uma mudança no backend aparecer no site.
 *
 * 30 segundos: teto de defasagem aceito para catálogo e configuração de loja
 * (era 5 minutos até 2026-08-19; baixou junto com o `s-maxage` do listing, que
 * também é 30). É o botão único — mexer aqui liga/desliga o Data Cache de
 * TODAS as leituras públicas de uma vez. `0` volta ao comportamento sem cache.
 *
 * O ganho real deste TTL está em CONCORRÊNCIA, não em tempo: com muitos
 * visitantes simultâneos, a primeira requisição de cada janela paga a API e as
 * demais são servidas de graça. Num tenant de tráfego baixo (~0,5 req/min) o
 * hit rate tende a zero — para esse caso o que resolve é invalidação por evento
 * (item 0C: webhook do portal → `revalidateTag`), que permite TTL longo sem
 * defasagem. As tags já saem prontas daqui, prefixadas pelo tenant.
 *
 * ATENÇÃO ao operar: o cache é POR POD e não sobrevive a deploy — são 2
 * réplicas sem `cacheHandler` compartilhado (`output: standalone`). Espere
 * aquecimento após cada rollout.
 */
export const PUBLIC_READ_REVALIDATE = 30;

/**
 * Busca NÃO entra no Data Cache (ver `search.service.ts`): o espaço de `q` é
 * infinito, então cada termo criaria uma entrada que nunca seria reusada — e o
 * usuário espera resultado fresco ao buscar.
 */
export const NO_CACHE = 0;

/**
 * Tempo máximo (ms) de uma leitura pública feita no SERVIDOR (SSR). Sem ele valia o padrão
 * do undici (minutos): com a API lenta, cada renderização ficava presa e elas se acumulavam
 * até o pod cair. Estourar vira `PublicReadError` (status 0), o mesmo caminho de "sem
 * resposta" — e quem trata erro (ex.: widgets com `.catch`) segue funcionando.
 * 8 s cobre a listagem mais pesada com folga (p95 da API em carga ~2 s no teste de 23/09).
 */
export const PUBLIC_READ_TIMEOUT_MS = 8_000;

/**
 * Esperas (ms) antes de cada nova tentativa de uma leitura no SERVIDOR que não
 * teve resposta por uma falha PASSAGEIRA — ver `TRANSIENT_NETWORK_CODES`. Duas
 * tentativas extras, curtas: um soluço de DNS (`EAI_AGAIN`) dura milissegundos
 * e, sem isto, um só bastava para mandar o visitante para /erro. Custo no pior
 * caso: ~1,3 s a mais numa renderização que de outro modo falharia.
 */
export const TRANSIENT_RETRY_DELAYS_MS = [250, 1_000] as const;

/**
 * Códigos de rede que valem nova tentativa: o alvo está certo e a falha some
 * sozinha. Ficam DE FORA, de propósito:
 * - timeout — cada tentativa já pode levar 8 s; repetir chegaria a 24 s;
 * - `ENOTFOUND`/`ECONNREFUSED` — nome inexistente e API parada não voltam em 1 s;
 * - qualquer resposta HTTP — a API respondeu, repetir não muda o resultado.
 */
const TRANSIENT_NETWORK_CODES = new Set(["EAI_AGAIN", "ECONNRESET", "UND_ERR_SOCKET"]);

/** Código de rede do undici — às vezes dentro de uma AggregateError (IPv4 + IPv6). */
function networkErrorCode(cause: unknown): string | undefined {
    const err = cause as { code?: unknown; errors?: Array<{ code?: unknown }> } | null | undefined;
    if (!err || typeof err !== "object") return undefined;
    if (typeof err.code === "string") return err.code;
    const first = err.errors?.[0]?.code;
    return typeof first === "string" ? first : undefined;
}

/** Espera com ±20% de variação: pods que falharam juntos não repetem juntos. */
function backoff(delayMs: number): Promise<void> {
    const jittered = delayMs * (0.8 + Math.random() * 0.4);
    return new Promise((resolve) => setTimeout(resolve, jittered));
}

/**
 * A nest exige `x-session-id` mesmo nos reads públicos (identifica anônimos),
 * mas o VALOR não muda a resposta destes endpoints.
 *
 * No servidor é a sessão de SERVIÇO do tenant (`service-session.ts`): uma por
 * processo, constante de propósito — um id por visitante personalizaria o
 * payload (é justamente o que estamos evitando), e como os headers entram na
 * chave do cache, um id variável daria a cada visitante uma entrada própria e
 * sempre fria. Não pode mais ser um valor inventado: a nest só aceita ids que
 * ela emitiu.
 */
const SESSION_INVALID_MESSAGE = /session id inv[aá]lido/i;

type QueryValue = string | number | boolean | null | undefined;

export interface PublicGetOptions {
    /** Query string. Valores `undefined`/`null` são omitidos. */
    params?: Record<string, QueryValue>;
    /** Tags do Data Cache. O suffix do tenant é prefixado automaticamente. */
    tags?: string[];
    /** Sobrepõe o `PUBLIC_READ_REVALIDATE` para este recurso. */
    revalidate?: number;
    /**
     * Versão da rota na nest (`/api/v{n}`). Default `1`; o catálogo novo é `2`.
     * Só no servidor — no browser o proxy `/api/nest` tem a base própria (v1).
     */
    version?: 1 | 2;
}

/**
 * Falha numa leitura pública, com o status HTTP preservado.
 *
 * `status === 0` = a requisição NÃO chegou a ter resposta (conexão recusada,
 * DNS, timeout, TLS). Nesse caso `cause` traz o erro original do runtime
 * (undici), cujo `code` — ECONNREFUSED, ENOTFOUND, ... — é o que diagnostica.
 * `url` é o alvo completo: sem ele um "fetch failed" não diz sequer para onde
 * o front tentou falar. `attempts` > 1 = a falha passageira se repetiu em
 * todas as tentativas (ver `TRANSIENT_RETRY_DELAYS_MS`).
 */
export class PublicReadError extends Error {
    constructor(
        readonly status: number,
        message: string,
        readonly path: string,
        readonly url?: string,
        cause?: unknown,
        readonly attempts = 1
    ) {
        super(message, cause === undefined ? undefined : { cause });
        this.name = "PublicReadError";
    }
}

function buildQuery(params: PublicGetOptions["params"]): string {
    if (!params) return "";
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        // `NaN`/`Infinity` virariam a string "NaN" na URL — e a nest responde
        // 400 ("x deve ser um número inteiro") para qualquer texto em parâmetro
        // numérico. Omitir é o mesmo que "não informado".
        if (typeof value === "number" && !Number.isFinite(value)) continue;
        query.set(key, String(value));
    }
    const qs = query.toString();
    return qs ? `?${qs}` : "";
}

/** Mensagem legível do envelope de erro da nest (`{ error: ... }`). */
function errorMessage(body: unknown, status: number, path: string): string {
    const payload = body as
        { error?: string | string[] | { message?: string | string[] } } | undefined;
    const raw =
        payload && typeof payload.error === "object" && !Array.isArray(payload.error)
            ? payload.error.message
            : payload?.error;

    return normalizeApiMessage(raw, `Falha ao carregar ${path} (HTTP ${status}).`);
}

/**
 * GET numa rota pública da nest.
 *
 * - **Servidor:** chama a nest direto, ANÔNIMO, com o tenant do request e o
 *   Data Cache do Next (revalidate + tags).
 * - **Browser:** chama o proxy same-origin `/api/nest`. O `x-session-id` vai
 *   pelo cookie (o proxy o lê de lá) e nada é cacheado — no cliente o Data
 *   Cache não existe, e a resposta não vira HTML compartilhado.
 */
export async function publicGet<T>(path: string, options: PublicGetOptions = {}): Promise<T> {
    const { params, tags = [], revalidate = PUBLIC_READ_REVALIDATE, version = 1 } = options;
    const query = buildQuery(params);

    const { response, url } =
        typeof window === "undefined"
            ? await serverFetch(path, query, tags, revalidate, version)
            : { response: await browserFetch(path, query), url: undefined };

    const body: unknown = await response.json().catch(() => null);

    if (!response.ok) {
        throw new PublicReadError(
            response.status,
            errorMessage(body, response.status, path),
            path,
            url
        );
    }

    return (isNestEnvelope(body) ? body.data : body) as T;
}

async function serverFetch(
    path: string,
    query: string,
    tags: string[],
    revalidate: number,
    version: 1 | 2
): Promise<{ response: Response; url: string }> {
    const clientSuffix = await getClientSuffix();
    const url = `${env.apiUrl}/api/v${version}${path}${query}`;

    const read = async (sessionId: string) =>
        fetch(url, {
            headers: {
                [CLIENT_SUFFIX_HEADER]: clientSuffix,
                [SESSION_COOKIE]: sessionId,
            },
            next: { revalidate, tags: tags.map((tag) => `${clientSuffix}:${tag}`) },
            signal: AbortSignal.timeout(PUBLIC_READ_TIMEOUT_MS),
        });

    for (let attempt = 0; ; attempt += 1) {
        try {
            let response = await read(await getServiceSessionId(clientSuffix));

            // A nest deixou de aceitar a sessão de serviço (regra mudou num
            // deploy): cria outra e repete UMA vez. Clona antes de ler o corpo —
            // o `response.json()` do chamador precisa do stream intacto.
            if (response.status === 400 && (await isSessionRejected(response.clone()))) {
                invalidateServiceSession(clientSuffix);
                response = await read(await getServiceSessionId(clientSuffix));
            }
            return { response, url };
        } catch (error) {
            // O fetch nativo lança um TypeError genérico ("fetch failed") e esconde
            // o motivo real em `cause`. Preserva-o junto com a URL: é o que o log
            // do bootstrap usa para dizer ao suporte O QUE checar.
            const cause = error instanceof Error && error.cause !== undefined ? error.cause : error;

            // Falha passageira (o catch cobre também a criação da sessão de
            // serviço): espera um pouco e repete a leitura inteira.
            const delay = TRANSIENT_RETRY_DELAYS_MS[attempt];
            if (delay !== undefined && TRANSIENT_NETWORK_CODES.has(networkErrorCode(cause) ?? "")) {
                await backoff(delay);
                continue;
            }

            throw new PublicReadError(
                0,
                `Sem resposta de ${url}: ${error instanceof Error ? error.message : String(error)}`,
                path,
                url,
                cause,
                attempt + 1
            );
        }
    }
}

/** true quando o 400 é especificamente a recusa do `x-session-id`. */
async function isSessionRejected(response: Response): Promise<boolean> {
    const body: unknown = await response.json().catch(() => null);
    const message = errorMessage(body, response.status, "");
    return SESSION_INVALID_MESSAGE.test(message);
}

/**
 * No browser não existe tenant nem Data Cache: fala-se só com o proxy
 * same-origin, que carimba o `x-client-suffix`.
 *
 * O `x-session-id` é garantido aqui (não só lido do cookie) porque a nest o
 * exige e o cookie pode ainda não existir no primeiríssimo acesso — o
 * `SessionInit` cria em paralelo, e sem esta espera uma interação rápida (abrir
 * o painel de categorias, digitar na busca) sairia sem o header. Mesma garantia
 * que o `http` (axios) dá; a promise é singleton no helper, então não cria duas.
 */
async function browserFetch(path: string, query: string): Promise<Response> {
    return fetch(`/api/nest${path}${query}`, {
        headers: { [SESSION_COOKIE]: await ensureSessionId() },
        cache: "no-store",
    });
}
